// Display table card in grid view

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, Clock, AlertCircle, DollarSign, Users, CheckCircle, XCircle, Wifi, WifiOff, DoorOpen } from 'lucide-react';
import { Mesa, MesaItem } from '../../../../types/mesa';
import { useRestaurant } from '../../../../providers/RestaurantContext';
import { toast } from 'react-toastify';

// Format currency with thousand separators
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

interface Props {
  mesa: Mesa;
  onSyncFromPOS: (mesaId: string) => void;
  isSyncing?: boolean;
  onRetrySync?: (mesaId: string) => void;
  onMesaClosed?: () => void;
}

const MesaCard: React.FC<Props> = ({ mesa, onSyncFromPOS, isSyncing = false, onRetrySync, onMesaClosed }) => {
  const [closing, setClosing] = useState(false);
  const { restaurant } = useRestaurant();
  // Pagado from Firestore (mesa.montoPagado). New payments store full amount; older data may be subtotal-only.
  const pagado = mesa.montoPagado ?? 0;
  const taxRate = restaurant?.settings?.taxRate || 18; // Default 18%
  // Total con impuesto: use synced value when present, else compute from items + tax
  const itemSubtotal = (mesa.items ?? []).reduce((s, i) => s + (i.quantity ?? 1) * (i.price ?? 0), 0);
  const total = mesa.montoTotalConImpuestos ?? (itemSubtotal > 0 ? itemSubtotal * (1 + taxRate / 100) : (mesa.montoTotal ?? 0) * (1 + taxRate / 100));
  // Restante = what's still owed so Total = Pagado + Restante (allows paying remainder; no hardcoding)
  const restante = Math.max(0, total - pagado);
  
  // ========== CRITICAL: Check if POS is CONFIGURED ==========
  const restaurantIntegrationMode = restaurant?.integrationMode || 'none';
  const posProvider = restaurant?.posConfig?.provider || 'none';
  const hasPOSConfigured = 
    restaurantIntegrationMode !== 'none' && 
    posProvider !== 'none';
  
  // Check if mesa has new schema fields
  const hasNewSchema = mesa.activeSource !== undefined && mesa.integrationMode !== undefined;
  
  // ========== CRITICAL: Check if ACTUALLY SYNCED (not just configured) ==========
  // A mesa is ACTUALLY synced if and only if:
  // 1. syncStatus === "ok" (backend confirmed successful sync)
  // 2. activeSource !== "manual" (not in fallback mode)
  const isActuallySynced = 
    mesa.syncStatus === 'ok' &&
    mesa.activeSource !== 'manual';
  
  // Determine states
  const isManualMode = mesa.activeSource === 'manual';
  
  // ✅ FIXED: Show retry button if restaurant has POS and mesa is NOT actually synced
  // This covers: failed syncs, never-synced mesas, stale syncs
  const canRetrySync = hasPOSConfigured && 
                       !isActuallySynced && 
                       mesa.integrationMode !== 'manual';
  
  // Check if sync data is stale (older than 5 minutes) - only when actually synced
  const isSyncStale = isActuallySynced && mesa.lastPosSync && typeof mesa.lastPosSync.toDate === 'function'
    ? (Date.now() - mesa.lastPosSync.toDate().getTime()) > 5 * 60 * 1000
    : false;

  // Calculate time elapsed since table was opened
  const getTimeElapsed = () => {
    if (!mesa.ultimaActualizacion) return null;
    const now = Date.now();
    const openedAt = mesa.ultimaActualizacion.toDate().getTime();
    const diffMinutes = Math.floor((now - openedAt) / (1000 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes}m`;
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  const timeElapsed = getTimeElapsed();

  // No active session: show empty mesa state (table remains visible, no session data)
  if (!mesa.activeSessionId) {
    return (
      <div className="group relative bg-zinc-900/60 rounded-xl border border-white/5 p-3 flex flex-col justify-between min-h-[96px] transition-all duration-200 hover:bg-zinc-800 hover:border-yap-primary/40">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold text-white tracking-tight">#{mesa.numeroMesa}</h2>
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-zinc-700/60 text-zinc-300 border border-white/5">
            <div className="w-2 h-2 rounded-full bg-zinc-400" />
            Libre
          </span>
        </div>
        <p className="text-xs text-white/50 mb-4">Esperando pedido</p>
        <button
          type="button"
          className="mb-2 w-full px-2 py-1.5 rounded-lg bg-zinc-700/60 border border-white/10 text-zinc-300 text-xs font-medium hover:bg-zinc-600/60 transition-colors flex items-center justify-center gap-1.5"
          onClick={() => toast.info('La mesa se abrirá al recibir un pedido desde el POS')}
        >
          <DoorOpen className="w-3.5 h-3.5" />
          Abrir mesa
        </button>
        <div className="space-y-1 mt-auto">
          <Link to={`/admin/mesa/${mesa.id}/resumen`} className="block">
            <button className="w-full py-1.5 text-[11px] font-semibold text-zinc-300 bg-zinc-800/80 rounded-md hover:text-white hover:bg-zinc-700 transition-colors">
              Detalles
            </button>
          </Link>
          <Link to={`/admin/mesa/${mesa.id}/qr`} className="block">
            <button className="w-full py-1.5 text-[11px] font-semibold text-black bg-yap-primary rounded-md hover:bg-yap-hover transition-all shadow-[0_0_10px_-3px_rgba(0,255,132,0.3)] hover:scale-[1.01] active:scale-95">
              QR
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative bg-zinc-900/60 rounded-xl border border-white/5 p-3 flex flex-col justify-between min-h-[96px] transition-all duration-200 hover:bg-zinc-800 hover:border-yap-primary/40">
      {/* Title and Status */}
      <div className="flex justify-between items-start mb-2">
        <h2 className="text-xl font-semibold text-white tracking-tight">#{mesa.numeroMesa}</h2>
        <div className="flex flex-col items-end gap-0.5">
          <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
            mesa.estado === 'abierta' 
              ? 'bg-yap-primary/20 text-yap-primary border border-yap-primary/30' 
              : 'bg-zinc-800/60 text-zinc-400 border border-white/5'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              mesa.estado === 'abierta' ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            {mesa.estado === 'abierta' ? 'Ocupada' : 'Cerrada'}
          </span>
          {timeElapsed && mesa.estado === 'abierta' && (
            <span className="text-[11px] text-zinc-500 font-medium">{timeElapsed}</span>
          )}
        </div>
      </div>

      {/* Reattachment banner: POS ticket still open after YAP closed session */}
      {mesa.reattachedFromPOS && (
        <div className="mb-2 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[11px] flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3 shrink-0" />
          <span>El ticket del POS para esta mesa sigue abierto.</span>
        </div>
      )}

      {/* Active Source Badge */}
      <div className="flex items-center gap-2 text-[11px] mb-2 pb-2 border-b border-white/5">
        <div className="flex items-center gap-2 flex-1">
          {!hasNewSchema ? (
            <>
              <AlertCircle className="w-3 h-3 text-yellow-500" />
              <span className="text-yellow-400 font-medium">
                Requiere actualización de esquema
              </span>
            </>
          ) : !hasPOSConfigured ? (
            <>
              <WifiOff className="w-3 h-3 text-zinc-500" />
              <span className="text-zinc-400 font-medium">
                Modo manual (sin POS configurado)
              </span>
            </>
          ) : isSyncing ? (
            <>
              <RefreshCw className="w-3 h-3 text-blue-500 animate-spin" />
              <span className="text-blue-400 font-medium">
                Actualizando...
              </span>
            </>
          ) : isActuallySynced && !isSyncStale ? (
            <>
              <Wifi className="w-3 h-3 text-green-500" />
              <span className="text-green-400 font-medium">
                Sincronizado con POS
              </span>
            </>
          ) : isSyncStale ? (
            <>
              <AlertCircle className="w-3 h-3 text-orange-500" />
              <span className="text-orange-400 font-medium">
                Sincronización desactualizada
              </span>
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3 text-zinc-500" />
              <span className="text-zinc-400 font-medium">
                Modo manual activo
              </span>
            </>
          )}
        </div>
        
        {/* Small Retry Icon Button - Only show when can retry */}
        {canRetrySync && onRetrySync && (
          <button
            onClick={() => onRetrySync(mesa.id)}
            disabled={isSyncing}
            className="p-1 rounded-md text-orange-400 hover:text-orange-300 hover:bg-orange-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Reintentar sincronización"
          >
            <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      {/* Totals */}
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
          <span className="text-zinc-400 font-medium">Total con impuesto</span>
          <span className="text-yap-primary font-semibold">RD${formatCurrency(total)}</span>
        </div>

        {/* Pagado */}
        <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
          <span className="text-zinc-400 font-medium">Pagado</span>
          <span className="text-green-400 font-semibold">RD${formatCurrency(pagado)}</span>
        </div>

        <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
          <span className="text-zinc-400 font-medium">Restante</span>
          <span className="text-red-400 font-semibold">RD${formatCurrency(restante)}</span>
        </div>

        {/* Artículos - ONLY show when ACTUALLY synced (not just configured) */}
        {isActuallySynced && mesa.items && mesa.items.length > 0 && (
          <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
            <span className="text-zinc-400 font-medium">Artículos</span>
            <span className="text-white font-semibold">{mesa.items.length}</span>
          </div>
        )}
      </div>

      {/* Mesa para cerrar - clickable: saves to pagos and blanks the mesa */}
      {restante === 0 && total > 0 && (
        <button
          type="button"
          disabled={closing || !restaurant?.id}
          onClick={async () => {
            if (!restaurant?.id) return;
            setClosing(true);
            try {
              const functions = getFunctions();
              const closeMesaFn = httpsCallable<{ restaurantId: string; mesaId: string }, { success: boolean }>(functions, 'closeMesa');
              await closeMesaFn({ restaurantId: restaurant.id, mesaId: mesa.id });
              toast.success('Mesa cerrada. Detalles guardados en pagos.');
              onMesaClosed?.();
            } catch (e: any) {
              const msg = e?.message || e?.code || 'Error al cerrar la mesa';
              toast.error(msg);
            } finally {
              setClosing(false);
            }
          }}
          className="mb-2 w-full px-2 py-1.5 rounded-lg bg-yap-primary/15 border border-yap-primary/30 text-yap-primary text-xs font-medium hover:bg-yap-primary/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {closing ? 'Cerrando…' : 'Cerrar mesa'}
        </button>
      )}

      {/* Actions */}
      <div className="space-y-1 mt-auto">
        <Link to={`/admin/mesa/${mesa.id}/resumen`} className="block">
          <button className="w-full py-1.5 text-[11px] font-semibold text-zinc-300 bg-zinc-800/80 rounded-md hover:text-white hover:bg-zinc-700 transition-colors">
            Detalles
          </button>
        </Link>

        {/* QR Code Button */}
        <Link to={`/admin/mesa/${mesa.id}/qr`} className="block">
          <button className="w-full py-1.5 text-[11px] font-semibold text-black bg-yap-primary rounded-md hover:bg-yap-hover transition-all shadow-[0_0_10px_-3px_rgba(0,255,132,0.3)] hover:scale-[1.01] active:scale-95">
            QR
          </button>
        </Link>
      </div>

      {/* Last Payment Timestamp */}
      {mesa.lastPaymentAt && (
        <div className="text-[10px] text-zinc-600 text-center mt-1.5">
          Último pago: {mesa.lastPaymentAt.toDate().toLocaleString('es-DO', { 
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
          })}
        </div>
      )}
    </div>
  );
};

export default MesaCard;

